import { useEffect, useRef, type PropsWithChildren } from "react";
import { useQuery } from "convex/react";
import { useAuthActions } from "@convex-dev/auth/react";
import { NavLink, useLocation } from "react-router-dom";
import { Activity, Ban, Braces, ClipboardCheck, KeyRound, Radar, Settings, Users } from "lucide-react";
import { api } from "../../convex/_generated/api";
import { FullScreenLoader } from "./ui";
import { WlsLogo } from "./WlsLogo";
import { AccountMenu } from "./AccountMenu";
import { countNewWatchAlerts, playWatchAlertSound } from "../lib/watchAlertSound";


type NavItem = { to: string; label: string; icon: typeof Activity; end?: boolean };

const primary: NavItem[] = [
  { to: "/", label: "Vue d’ensemble", icon: Activity, end: true },
  { to: "/blacklist", label: "Blacklist", icon: Ban },
  { to: "/controles", label: "Contrôles", icon: ClipboardCheck },
  { to: "/surveillance", label: "Surveillance", icon: Radar },
];
const admin: NavItem[] = [
  { to: "/admin/utilisateurs", label: "Utilisateurs", icon: Users },
  { to: "/admin/api", label: "Clés API", icon: KeyRound },
  { to: "/admin/audit", label: "Logs", icon: Braces },
  { to: "/admin/parametres", label: "Paramètres", icon: Settings },
];

export function AppShell({ children }: PropsWithChildren) {
  const me = useQuery(api.users.me);
  const { signOut } = useAuthActions();
  const location = useLocation();
  // Un visiteur n'a pas accès à ces sections : interroger leurs compteurs lui vaudrait un refus
  // du serveur à chaque chargement de page.
  const staff = me ? me.role !== "visitor" : false;
  const recentEvasions = useQuery(api.suspects.recentEvasionCount, staff ? {} : "skip");
  const pendingAlerts = useQuery(api.watches.pendingAlertCount, staff ? {} : "skip");
  const watches = useQuery(api.watches.list, staff ? {} : "skip");
  const previousAlertCounts = useRef<Map<string, number> | undefined>(undefined);

  // L'alerte sonore vit dans la coquille, pas dans la page Surveillance : un nouveau joueur
  // détecté doit s'entendre même quand on travaille sur la blacklist ou un contrôle.
  useEffect(() => {
    if (watches === undefined) return;
    const current = watches.map((watch) => ({ id: watch._id, alerts: watch.alerts }));
    const newAlerts = countNewWatchAlerts(previousAlertCounts.current, current);
    previousAlertCounts.current = new Map(current.map((watch) => [watch.id, watch.alerts]));
    if (newAlerts > 0) void playWatchAlertSound();
  }, [watches]);

  // `me === null` signifie que le compte a été désactivé ou supprimé pendant la session :
  // sans cette déconnexion, l'utilisateur reste bloqué sur un écran de chargement sans issue.
  useEffect(() => { if (me === null) void signOut(); }, [me, signOut]);

  if (me === undefined) return <FullScreenLoader label="Chargement de votre espace…" />;
  if (me === null) return <FullScreenLoader label="Session expirée, redirection…" />;

  // Un visiteur consulte la blacklist et la vue d'ensemble, rien d'autre. L'administration
  // (comptes, clés API, réglages) n'appartient qu'au propriétaire de l'outil.
  const links = me.role === "visitor"
    ? primary.filter((item) => item.to === "/" || item.to === "/blacklist")
    : me.isOwner ? [...primary, ...admin] : primary;
  return (
    <div className="app-shell">
      <a className="skip-link" href="#contenu">Aller au contenu</a>
      <header className="topbar">
        <NavLink to="/" className="brand" aria-label="Blacklist WLS — Accueil">
          <WlsLogo size={30} />
          <span><strong>Blacklist</strong><small>War Legend</small></span>
        </NavLink>
        <nav className="main-nav" aria-label="Navigation principale">
          {links.map(({ to, label, icon: Icon, end }) => (
            // Le libellé est masqué sur les écrans étroits, ce qui le retire aussi de l'arbre
            // d'accessibilité : `title` et `aria-label` gardent l'onglet nommé et survolable.
            <NavLink key={to} to={to} end={end} title={label} aria-label={label}
              className={({ isActive }) => isActive ? "nav-link nav-link--active" : "nav-link"}>
              <Icon size={16} aria-hidden="true" /><span>{label}</span>
              {to === "/controles" && recentEvasions ? <em className="nav-badge" aria-label={`${recentEvasions} contournement(s) à voir`}>{recentEvasions}</em> : null}
              {to === "/surveillance" && pendingAlerts ? <em className="nav-badge" aria-label={`${pendingAlerts} alertes à traiter`}>{pendingAlerts}</em> : null}
            </NavLink>
          ))}
        </nav>
        <AccountMenu me={me} />
      </header>
      <main id="contenu" key={location.pathname} className="main-content">{children}</main>
    </div>
  );
}
